import React from 'react';
import { Instagram, Linkedin, Twitter, Lock } from 'lucide-react';

interface FooterProps {
    onNavigate: (page: 'home' | 'blog' | 'contact' | 'admin-login' | 'resources' | 'privacy' | 'white-label' | 'private-label' | 'influencers' | 'about' | 'certifications', sectionId?: string) => void;
}

const Footer: React.FC<FooterProps> = ({ onNavigate }) => {

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string, sectionId?: string) => {
    e.preventDefault();
    if (href === '/resources' && sectionId) {
        onNavigate('resources', sectionId);
    } else if (href.startsWith('#')) {
        onNavigate('home', href.substring(1));
    } else if (href === '/blog') {
        onNavigate('blog');
    } else if (href === '/contact') {
        onNavigate('contact');
    } else if (href === '/admin') {
        onNavigate('admin-login');
    } else if (href === '/privacy') {
        onNavigate('privacy');
    } else if (href === '/white-label-supplements') {
        onNavigate('white-label');
    } else if (href === '/private-label-supplements') {
        onNavigate('private-label');
    } else if (href === '/supplements-for-influencers') {
        onNavigate('influencers');
    } else if (href === '/about') {
        onNavigate('about');
    } else if (href === '/certifications') {
        onNavigate('certifications');
    }
  };

  return (
    <footer className="bg-gray-50 pt-16 pb-8 border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          
          <div className="col-span-2 md:col-span-1">
            <span className="text-xl font-extrabold tracking-tight text-black block mb-4">
              BuildMy<span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-700">Supplement</span>
            </span>
            <p className="text-sm text-gray-600 mb-6">
              Built for serious health brands. Manufactured with care. Partnered for growth.
            </p>
            <div className="flex space-x-4">
               <a href="#" aria-label="Instagram" className="text-gray-400 hover:text-orange-500"><Instagram size={20} /></a>
               <a href="#" aria-label="LinkedIn" className="text-gray-400 hover:text-orange-500"><Linkedin size={20} /></a>
               <a href="#" aria-label="Twitter" className="text-gray-400 hover:text-orange-500"><Twitter size={20} /></a>
            </div>
          </div>

          <div>
            <h3 className="font-bold text-gray-900 mb-4">Company</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li><a href="/about" onClick={(e) => handleLinkClick(e, '/about')} className="hover:text-orange-500">About Us</a></li>
              <li><a href="/certifications" onClick={(e) => handleLinkClick(e, '/certifications')} className="hover:text-orange-500">Certifications & Quality</a></li>
              <li><a href="/white-label-supplements" onClick={(e) => handleLinkClick(e, '/white-label-supplements')} className="hover:text-orange-500">White Label Supplements</a></li>
              <li><a href="/private-label-supplements" onClick={(e) => handleLinkClick(e, '/private-label-supplements')} className="hover:text-orange-500">Private Label Supplements</a></li>
              <li><a href="/supplements-for-influencers" onClick={(e) => handleLinkClick(e, '/supplements-for-influencers')} className="hover:text-orange-500">For Influencers</a></li>
              <li><a href="/contact" onClick={(e) => handleLinkClick(e, '/contact')} className="hover:text-orange-500">Contact Us</a></li>
              <li><a href="/blog" onClick={(e) => handleLinkClick(e, '/blog')} className="hover:text-orange-500">Blog & Insights</a></li>
              <li><a href="/admin" onClick={(e) => handleLinkClick(e, '/admin')} className="hover:text-orange-600 flex items-center mt-4 text-xs text-gray-500"><Lock className="w-3 h-3 mr-1" /> Admin Access</a></li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-gray-900 mb-4">Products</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li><a href="#products" onClick={(e) => handleLinkClick(e, '#products')} className="hover:text-orange-500">Protein Powders & Sports Nutrition</a></li>
              <li><a href="#products" onClick={(e) => handleLinkClick(e, '#products')} className="hover:text-orange-500">Vitamins & Minerals</a></li>
              <li><a href="#products" onClick={(e) => handleLinkClick(e, '#products')} className="hover:text-orange-500">Ayurvedic & Herbal</a></li>
              <li><a href="#products" onClick={(e) => handleLinkClick(e, '#products')} className="hover:text-orange-500">Functional Wellness</a></li>
              <li><a href="#products" onClick={(e) => handleLinkClick(e, '#products')} className="hover:text-orange-500">Custom Formulations</a></li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-gray-900 mb-4">Resources</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li><a href="/resources" onClick={(e) => handleLinkClick(e, '/resources', 'fssai')} className="hover:text-orange-500">FSSAI Compliance Guide</a></li>
              <li><a href="/resources" onClick={(e) => handleLinkClick(e, '/resources', 'startup-guide')} className="hover:text-orange-500">Starting a Brand</a></li>
              <li><a href="/resources" onClick={(e) => handleLinkClick(e, '/resources', 'formulation')} className="hover:text-orange-500">Formulation 101</a></li>
              <li><a href="/resources" onClick={(e) => handleLinkClick(e, '/resources', 'channels')} className="hover:text-orange-500">Sales Channels</a></li>
              <li><a href="/resources" onClick={(e) => handleLinkClick(e, '/resources', 'pricing')} className="hover:text-orange-500">Pricing & MOQ Info</a></li>
              <li><a href="/resources" onClick={(e) => handleLinkClick(e, '/resources', 'glossary')} className="hover:text-orange-500">Founder's Glossary</a></li>
            </ul>
          </div>

        </div>

        <div className="border-t border-gray-200 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-sm text-gray-500">© 2025 BuildMySupplement. All Rights Reserved.</p>
          <div className="flex space-x-6 mt-4 md:mt-0 text-sm text-gray-500">
            <a href="/privacy" onClick={(e) => handleLinkClick(e, '/privacy')} className="hover:text-black cursor-pointer">Privacy Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;